import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import {
  Box,
  Stack,
  Typography,
  Fade,
  CardHeader,
  useTheme,
  Button,
  useColorScheme
} from "@mui/material";
import {
  CreditCard as CreditCardIcon,
  DeleteOutline as DeleteIcon
} from "@mui/icons-material";
import useSWR from "swr";

import { useStore } from "../../../../service/mobx";
import { fetcher } from "../../../../service/graph";
import LongPressIconButton from "../../../Button/LongPress";
import ToolTip from "../../../Tooltip";
import Skeleton from "../../../Skeleton";

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY);

export default function UpdatePaymentDialog() {
  const [refetch, setRefetch] = useState(0);
  const [loading, setLoading] = useState(true);
  const { isLoading, data } = useSWR(
    `/api/stripe?view=card&t=${refetch}`,
    fetcher
  );

  return (
    <Stack spacing={2}>
      <CurrentCard
        card={data?.card}
        loading={loading}
        isLoading={isLoading}
        setLoading={setLoading}
        setRefetch={setRefetch}
      />
      <ReplaceCard
        card={data?.card}
        loading={loading}
        setLoading={setLoading}
        setRefetch={setRefetch}
      />
    </Stack>
  );
}

function CurrentCard({ card, isLoading, loading, setLoading, setRefetch }) {
  const { snackbar } = useStore();

  return (
    <Stack spacing={1}>
      <Typography variant="labelLg" color="var(--secondary-color)">
        Your payment method
      </Typography>
      {isLoading ? (
        <Skeleton width="100%" height={76} />
      ) : (
        <CardHeader
          avatar={
            <CreditCardIcon
              sx={{
                color: card
                  ? "var(--secondary-color)"
                  : "var(--tertiary-color)",
                opacity: 0.8
              }}
            />
          }
          title={
            card ? `${card?.brand} •••• ${card?.last4}` : "No card on file"
          }
          subheader={
            card
              ? `Expires ${card?.exp_month}/${card?.exp_year}`
              : " Please add a payment method"
          }
          action={
            card ? (
              <ToolTip title="Hold to delete" placement="left">
                <LongPressIconButton
                  disabled={loading}
                  onLongPress={async () => {
                    try {
                      setLoading(true);

                      const res = await fetch("/api/stripe", {
                        method: "DELETE",
                        body: JSON.stringify({ paymentMethodId: card?.id })
                      });

                      if (res.ok === false) {
                        const { error } = await res.json();

                        throw new Error(error);
                      }

                      setRefetch(count => count + 1);
                    } catch (error) {
                      console.error(error);

                      snackbar.notify({
                        duration: 60e3,
                        line1: "Failed to delete",
                        line2: "Please email us at support@bytez.com"
                      });
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  <DeleteIcon sx={{ color: "var(--tertiary-color)" }} />
                </LongPressIconButton>
              </ToolTip>
            ) : undefined
          }
          slotProps={{
            title: {
              variant: "titleMd",
              fontWeight: card ? undefined : 600,
              textTransform: card ? "capitalize" : undefined,
              color: card ? undefined : "var(--tertiary-color)"
            },
            subheader: {
              variant: "bodyMd",
              fontWeight: card ? undefined : 500,
              color: card ? undefined : "var(--tertiary-on-container)"
            }
          }}
          sx={{
            borderRadius: "var(--shape-sm)",
            bgcolor: card
              ? "var(--surface-container-lowest)"
              : theme => theme.addAlpha("var(--tertiary-container)", 0.5),
            "& .MuiCardHeader-action": {
              m: 0
            }
          }}
        />
      )}
    </Stack>
  );
}

function ReplaceCard({ card, loading, setLoading, setRefetch }) {
  const [clientSecret, setClientSecret] = useState();
  const theme = useTheme();
  const { mode, systemMode } = useColorScheme();
  const colors =
    theme.colorSchemes[
      mode === "dark" || systemMode === "dark" ? "dark" : "light"
    ];

  useEffect(() => {
    // Initialize the SetupIntent so we can add *new* cards
    fetch("/api/stripe?view=setup").then(async res => {
      const { clientSecret } = await res.json();

      setClientSecret(clientSecret);
      setLoading(false);
    });
  }, [setLoading]);

  return clientSecret ? (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: "flat",
          labels: "floating",
          variables: {
            borderRadius: theme.shape.md.round,
            fontSizeSm: 0,
            fontSizeXs: 0,
            fontFamily: theme.typography.fontFamily,
            colorBackground: colors.surface.container.color,
            colorPrimary: colors.secondary.color,
            colorText: colors.secondary.color,
            colorTextPlaceholder: colors.secondary.color,
            colorTextSecondary: colors.secondary.color
          },
          rules: {
            ".AccordionItem": {
              border: 0,
              padding: "8px 8px 0 8px",
              backgroundColor: colors.surface.container.high
            }
          }
        }
      }}
    >
      <CollectCard
        card={card}
        loading={loading}
        setLoading={setLoading}
        setRefetch={setRefetch}
      />
    </Elements>
  ) : null;
}

function CollectCard({ card, setLoading, loading, setRefetch }) {
  const [errorMessage, setErrorMessage] = useState();
  const stripe = useStripe();
  const elements = useElements();
  const { dialog, snackbar } = useStore();

  return (
    <Stack
      component="form"
      spacing={3}
      onSubmit={async event => {
        try {
          event.preventDefault();

          setLoading(true);
          setErrorMessage();

          // Confirm setup (adds card to Stripe)
          const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            redirect: "if_required",
            confirmParams: { return_url: window.location.href }
          });

          if (error) {
            setErrorMessage(error.message);
          } else {
            // SUCCESS: Now set this new card as default on your backend
            await fetch("/api/stripe", {
              method: "PUT",
              body: JSON.stringify({
                paymentMethodId: setupIntent.payment_method
              })
            });

            setRefetch(count => count + 1);
            dialog.set.open(false);
            snackbar.notify({ text: "Payment method updated" });
          }
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      }}
    >
      {/* New Card Section */}
      <Stack spacing={1}>
        <Typography variant="labelLg" color="var(--secondary-color)">
          {card ? "Replace with new card" : "Add payment method"}
        </Typography>
        <PaymentElement />
      </Stack>

      {/* Error Message */}
      {errorMessage ? (
        <Fade in>
          <Box
            p={1.5}
            borderRadius="var(--shape-sm)"
            bgcolor="var(--error-container)"
            color="var(--error-on-container)"
          >
            <Typography variant="bodySm">{errorMessage}</Typography>
          </Box>
        </Fade>
      ) : null}
      <Button
        fullWidth
        type="submit"
        variant="contained"
        loading={loading}
        loadingPosition="start"
        sx={{
          height: 56,
          fontSize: "16px !important",
          borderRadius: "var(--shape-sm)"
        }}
      >
        {loading ? "Saving..." : card ? "Save Card" : "Add Card"}
      </Button>
    </Stack>
  );
}
